import { Info, MinusCircle } from "lucide-react";

/**
 * Two plain lines on the send preview (Corbin approved 10/21):
 *   · "Reports will go as a link — this one is too big to attach." when the
 *     packet's reports will not fit in one email (server: packetSizePlan.ts);
 *   · "Left out: blank form" / "Left out: city copy" for files that never go
 *     to the customer (server: customerPacketLeftOut.ts).
 * Nothing to click and nothing new to learn. Renders nothing when neither applies.
 */

export interface LeftOutFile {
    id: string;
    fileName: string;
    reason: "blank_form" | "city_copy";
    label: string;
}

export interface PacketSizePlan {
    reportsAsLink: boolean;
    reportCount: number;
    totalBytes: number;
    leftOut: LeftOutFile[];
    message: string | null;
}

export function PacketSizeNotice({
    message,
    leftOut,
}: {
    message: string | null | undefined;
    leftOut: LeftOutFile[] | null | undefined;
}) {
    const left = leftOut ?? [];
    if (!message && left.length === 0) return null;
    const groups = groupLeftOut(left);
    return (
        <div className="flex flex-col gap-1.5" data-testid="packet-size-notice">
            {message && (
                <div
                    className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-[13.5px] leading-snug text-blue-900 dark:bg-blue-950/50 dark:text-blue-100"
                    data-testid="reports-as-link"
                >
                    <Info className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
                    <span>{message}</span>
                </div>
            )}
            {groups.map((g) => (
                <div
                    key={g.key}
                    className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-[13px] leading-snug text-foreground/80"
                    data-testid="left-out"
                >
                    <MinusCircle className="mt-0.5 h-4 w-4 flex-none text-foreground/50" aria-hidden />
                    <span className="min-w-0 break-words">
                        <b className="font-semibold text-foreground">{g.title}</b>
                        {g.note && <span> {g.note}</span>}
                        {g.names && (
                            <>
                                <br />
                                <span className="text-foreground/60">{g.names}</span>
                            </>
                        )}
                    </span>
                </div>
            ))}
        </div>
    );
}

/**
 * One line per kind, so a scan reads it at a glance: every blank form by
 * name (someone uploaded it by mistake), city copies as one count.
 */
export function groupLeftOut(
    left: LeftOutFile[],
): Array<{ key: string; title: string; note: string | null; names: string | null }> {
    const out: Array<{ key: string; title: string; note: string | null; names: string | null }> = [];
    for (const l of left.filter((x) => x.reason === "blank_form")) {
        out.push({ key: l.id, title: l.label, note: null, names: l.fileName });
    }
    const city = left.filter((x) => x.reason === "city_copy");
    if (city.length === 1) {
        out.push({
            key: "city",
            title: "Left out: city copy",
            note: "(the regular report goes)",
            names: city[0]!.fileName,
        });
    } else if (city.length > 1) {
        out.push({
            key: "city",
            title: `Left out: ${city.length} city copies`,
            note: "(the regular reports go)",
            names: null,
        });
    }
    return out;
}
